// src/store/middleware/errorMiddleware.ts
import { Middleware } from '@reduxjs/toolkit';
import { addNotification } from '../slices/uiSlice';
import i18n from '../../i18n/config';

export const errorMiddleware: Middleware = (store) => (next) => (action: any) => {
	const result = next(action);

	// Handle rejected async thunks
	if (action.type.endsWith('/rejected')) {
		const errorMessage = action.error?.message || 'An unexpected error occurred';

		// Don't show notifications for auth errors or specific errors
		const skipNotificationActions = [
			'auth/getCurrentUser/rejected',
			'organization/fetchUserOrganization/rejected'
		];

		if (!skipNotificationActions.includes(action.type)) {
			store.dispatch(addNotification({
				type: 'error',
				title: 'Error',
				message: errorMessage,
				duration: 5000,
			}));
		}
	}

	// Handle successful operations
	if (action.type.endsWith('/fulfilled')) {
		const successActions = {
			'auth/login/fulfilled': i18n.t('notifications.' + 'auth/login/fulfilled'),
			'auth/register/fulfilled': i18n.t('notifications.' + 'auth/register/fulfilled'),
			'organization/createUserOrganization/fulfilled': i18n.t('notifications.' + 'organization/createUserOrganization/fulfilled'),
			'organization/updateUserOrganization/fulfilled': i18n.t('notifications.' + 'organization/updateUserOrganization/fulfilled'),
			'branch/createNewBranch/fulfilled': i18n.t('notifications.' + 'branch/createNewBranch/fulfilled'),
			'branch/updateBranchData/fulfilled': i18n.t('notifications.' + 'branch/updateBranchData/fulfilled'),
			'branch/deleteBranchData/fulfilled': i18n.t('notifications.' + 'branch/deleteBranchData/fulfilled'),
			'branch/publishBranch/fulfilled': i18n.t('notifications.' + 'branch/publishBranch/fulfilled'),
			'category/createNewCategory/fulfilled': i18n.t('notifications.' + 'category/createNewCategory/fulfilled'),
			'category/updateCategoryData/fulfilled': i18n.t('notifications.' + 'category/updateCategoryData/fulfilled'),
			'category/deleteCategoryData/fulfilled': i18n.t('notifications.' + 'category/deleteCategoryData/fulfilled'),
			'menuItem/createNewMenuItem/fulfilled': i18n.t('notifications.' + 'menuItem/createNewMenuItem/fulfilled'),
			'menuItem/updateMenuItemData/fulfilled': i18n.t('notifications.' + 'menuItem/updateMenuItemData/fulfilled'),
			'menuItem/deleteMenuItemData/fulfilled': i18n.t('notifications.' + 'menuItem/deleteMenuItemData/fulfilled'),
			'announcements/createAnnouncement/fulfilled': i18n.t('announcements.createSuccess', 'Announcement created successfully'),
			'announcements/updateAnnouncement/fulfilled': i18n.t('announcements.updateSuccess', 'Announcement updated successfully'),
			'announcements/deleteAnnouncement/fulfilled': i18n.t('announcements.deleteSuccess', 'Announcement deleted successfully'),
		};

		// Skip notifications for fetch/read operations
		const skipSuccessNotificationActions = [
			'announcements/fetchAnnouncements/fulfilled',
		];

		const message = successActions[action.type as keyof typeof successActions];
		if (message) {
			store.dispatch(addNotification({
				type: 'success',
				title: i18n.t('common.success'),
				message,
				duration: 3000,
			}));
		}

		// Handle responses with { success: true } even if not in successActions list
		// But skip fetch operations
		if (!message && !skipSuccessNotificationActions.includes(action.type) && action.payload?.success === true) {
			store.dispatch(addNotification({
				type: 'success',
				title: i18n.t('common.success'),
				message: action.payload?.message || i18n.t('common.operationSuccessful') || 'Operation completed successfully',
				duration: 3000,
			}));
		}
	}

	return result;
};